import { useEffect } from 'react';
import { toast } from 'react-toastify';
import {RULE_MODEL} from "../Model/Model";
import {useDispatch, useSelector} from "react-redux";
import {errorMsg} from "../../../../../../../../Apis/functions";
import {
    CreatePayrollPolicyGroup,
    GetOvertimePolicyGroupConfig
} from "../../../../../../../../reduxToolkit/PayrollPolicy/PayrollPolicyApi";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Logic hook for the create overtime policy flow.
 * Handles form submission, navigation, and field-clearing actions.
 * @param {Function} t - i18n translation function.
 * @param {Object} item - Current policy form model.
 * @param {Function} setItem - Setter for the policy model.
 * @param {Function} navigate - React Router navigate function.
 * @param {boolean} isGlobal - Whether the policy applies globally.
 * @param {Function} setEmptyFields - Setter for the empty-fields validation flag.
 * @returns {Object} Handlers for create, back, and clear actions.
 */
const CreatePolicyApp = ({ t, item, setItem, departments, isGlobal, setEmptyFields, setRuleEmptyFields, setRule, setCleanChipSearch, goBack }) => {
    const dispatch = useDispatch();
    const {getOvertimePolicyGroupConfig} = useSelector((state) => state?.PayrollPolicySlice);

    /**
     * On mount, loads the system overtime policy group config and uses its
     * conflict resolution / stack method as the inherited defaults. Existing
     * (user-selected) values are preserved.
     */
    useEffect(() => {
        dispatch(GetOvertimePolicyGroupConfig()).then((res) => {
            const { data, success } = res?.payload?.data ?? {};
            if (!success || !data) return;
            setItem((prev) => prev.setConflictConfig(data));
        });
    }, []);

    /**
     * Validates the policy details and submits the create request.
     * Shows a warning toast if required fields are missing.
     */
    const handleCreate = () => {
        dispatch(CreatePayrollPolicyGroup(item.toRegister({ departments }))).then(
            ({payload: {data: { success, code }}}) => {
                if (success === true) {
                    toast.success(t('has_been_created_successfully'));
                    goBack();
                }else{
                    errorMsg(code);
                }
            },
        );
    };

    /**
     * Resets the policy details fields (name, isGlobal) while preserving the rules.
     */
    const handleClearDetails = () => {
        setItem((prev) => prev.clearDetails(isGlobal).setConflictConfig(getOvertimePolicyGroupConfig));
        setCleanChipSearch(true);
        setEmptyFields(false);
    };

    /**
     * Resets the policy rules to their initial state while preserving the details.
     */
    const handleClearRules = () => {
        setItem((prev) => prev.clearRules(isGlobal));
        setRule({...RULE_MODEL.clear()});
        setRuleEmptyFields(false);
    };

    return {
        handleCreate,
        handleClearDetails,
        handleClearRules
    };
};

export default CreatePolicyApp;
